import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomValidators } from 'angular-custom-validators';
import { EmergencyPhoneInfo } from 'src/app/interfaces';
import { dataStore } from 'src/app/app.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { UserSettingsService } from 'src/app/services/user-settings.service';

export interface DialogData {
  type: 'phone' | 'email';
}

@Component({
  selector: 'app-contact-edit',
  templateUrl: './contact-edit.component.html',
  styleUrls: ['./contact-edit.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgxIntlTelInputModule,
    MatError,
    MatIconButton,
    MatIcon,
    MatButton,
    MatDialogActions,
    MatDialogClose,
  ],
})
export class ContactEditComponent implements OnInit {
  userForm: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl('', [CustomValidators.email]),
    phoneNumber: new UntypedFormControl('', [CustomValidators.phone]),
  });
  phoneForm: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    phoneNumber: new UntypedFormControl('', [Validators.required, CustomValidators.phone]),
    description: new UntypedFormControl('', [Validators.required]),
  });
  emergencyPhoneNumbers = signal<EmergencyPhoneInfo[]>(
    dataStore.orgUser.emergencyPhoneNumbers
      ? structuredClone(dataStore.orgUser.emergencyPhoneNumbers)
      : [],
  );
  addingPhoneNumber = signal(false);

  loading = signal(true);
  awaitingResponse = signal(false);

  constructor(
    public dialogRef: MatDialogRef<ContactEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private snackBar: MatSnackBar,
    private userSettingsService: UserSettingsService,
  ) {}

  ngOnInit(): void {
    // console.log('test', this.data.type);
    this.userForm.patchValue(dataStore.orgUser);
    if (this.data.type === 'phone') {
      this.userForm.controls.phoneNumber.disable();
    } else if (this.data.type === 'email') {
      this.userForm.controls.email.disable();
    } else {
      this.userForm.controls.phoneNumber.disable();
      this.userForm.controls.email.disable();
    }
    this.loading.set(false);
  }

  managePhoneNumber(type: string, id?: number) {
    this.emergencyPhoneNumbers.update((numbers) =>
      numbers.map((phoneInfo) => ({ ...phoneInfo, editing: false })),
    );
    this.addingPhoneNumber.set(false);
    this.phoneForm.reset();
    switch (type) {
      case 'edit':
        this.phoneForm.patchValue(this.emergencyPhoneNumbers()[id]);
        this.emergencyPhoneNumbers.update((numbers) =>
          numbers.map((n, i) => (i === id ? { ...n, editing: true } : n)),
        );
        break;
      case 'add':
        this.addingPhoneNumber.set(true);
        break;
      case 'delete':
        this.emergencyPhoneNumbers.update((numbers) => [
          ...numbers.slice(0, id),
          ...numbers.slice(id + 1),
        ]);
        break;
    }
  }

  savePhoneNumber(type: string, id?: number) {
    const saveObj = {
      ...this.phoneForm.value,
      editing: false,
    };
    switch (type) {
      case 'edit':
        this.emergencyPhoneNumbers.update((numbers) =>
          numbers.map((n, i) => (i === id ? saveObj : n)),
        );
        break;
      case 'add':
        this.emergencyPhoneNumbers.update((numbers) => [...numbers, saveObj]);
        this.addingPhoneNumber.set(false);
        break;
    }
  }

  async save() {
    if (this.awaitingResponse() || !this.userForm.valid) {
      this.userForm.markAllAsTouched();
      return;
    }
    this.awaitingResponse.set(true);
    // if (this.data.type === 'phone') {
    //   if (dataStore.rootUser.email !== formValue.email) {
    //     const httpUpdateUser = httpsCallable(functions, 'httpUpdateUser');
    //     try {
    //       await httpUpdateUser({
    //         type: 'email',
    //         userId: dataStore.orgUser.id,
    //         orgId: localStorage.getItem('orgId'),
    //         emailNew: formValue.email,
    //         emailOld: dataStore.orgUser.email,
    //       });
    //       await setDoc(
    //         doc(db, `users/${localStorage.getItem('userId')}`),
    //         { email: formValue.email },
    //         { merge: true }
    //       );
    //       await setDoc(
    //         doc(
    //           db,
    //           `organisations/${localStorage.getItem(
    //             'orgId'
    //           )}/users/${localStorage.getItem('userId')}`
    //         ),
    //         { email: formValue.email },
    //         { merge: true }
    //       );
    //     } catch (error) {
    //       this.snackBar.open(
    //         'Er is iets misgegaan, probeer het later opnieuw of neem contact op met de beheerder',
    //         'X'
    //       );
    //       console.log('error', error);
    //       this.loading = false;
    //       return;
    //     }
    //   }
    //   if (dataStore.orgUser.phoneNumber !== formValue.phone) {
    //     await setDoc(
    //       doc(db, `users/${localStorage.getItem('userId')}`),
    //       { phoneNumber: formValue.phone },
    //       { merge: true }
    //     );
    //     await setDoc(
    //       doc(
    //         db,
    //         `organisations/${localStorage.getItem(
    //           'orgId'
    //         )}/users/${localStorage.getItem('userId')}`
    //       ),
    //       { phoneNumber: formValue.phone },
    //       { merge: true }
    //     );
    //   }
    // } else {
    const saveObj = {};

    if (!this.userForm.controls.email.disabled) {
      saveObj['email'] = this.userForm.controls.email.value;
    }
    if (!this.userForm.controls.phoneNumber.disabled) {
      saveObj['phoneNumber'] = this.userForm.controls.phoneNumber.value;
      saveObj['phoneNumber'] = saveObj['phoneNumber'].replace(/[\s-]/g, '');
      if (saveObj['phoneNumber'].startsWith('0')) {
        saveObj['phoneNumber'] = saveObj['phoneNumber'].replace('0', '+31');
      }
    }

    saveObj['emergencyPhoneNumbers'] = this.emergencyPhoneNumbers().map((phoneInfo) => {
      const pushObj = {
        name: phoneInfo.name,
        phoneNumber: phoneInfo.phoneNumber.replace(/[\s-]/g, ''),
        description: phoneInfo.description,
      };
      if (pushObj.phoneNumber.startsWith('0')) {
        pushObj.phoneNumber = pushObj.phoneNumber.replace('0', '+31');
      }
      return pushObj;
    });

    try {
      await this.userSettingsService.update(
        dataStore.rootUser.organisations.map((organisation) => organisation.id),
        saveObj,
      );

      this.snackBar.open('De gegevens zijn aangepast.', 'X');
      this.dialogRef.close(true);
    } catch (error) {
      this.awaitingResponse.set(false);
      this.snackBar.open(
        'Er is iets misgegaan, probeer het later opnieuw of neem contact op met de beheerder',
        'X',
      );
      console.error(error);
    }
  }
}
